import React, { useState, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Brain,
    History,
    Scroll,
    Settings,
    LogOut,
    User,
    LayoutDashboard,
    Zap,
    HelpCircle,
    Menu,
    X,
    Sparkles,
    Shield,
    AlertTriangle,
    AlertCircle,
    Layers,
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import ProfileDialog from '../common/ProfileDialog';
import SettingsDialog from '../common/SettingsDialog';
import Footer from './Footer';
import { OnboardingTour } from '../common/OnboardingTour';
import { confirmDiscardPendingWritersRoomChanges } from '../../utils/pendingChangesGuard';

export const Shell = ({ children }) => {
    const { user, logout, completeOnboarding } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const navItems = [
        { name: 'Ecosystem Hub', path: '/hub', icon: Layers },
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'Writers Room', path: '/writers-room', icon: Scroll },
    ];

    // docs/futuregoals.md FG-6: was `user?.plan_tier === 'Endovelicus'`, which
    // only checks one of the backend's two admin paths (core.access_control.
    // is_any_admin also grants access via the verified-admin-email
    // allowlist). That mismatch meant an email-allowlisted admin without an
    // Endovelicus tier passed every real /api/admin/* check but never saw
    // this link at all. `is_admin` is computed server-side from the same
    // is_any_admin() check admin_required already uses (GET /api/auth/me).
    const adminLink = user?.is_admin
        ? [{ name: 'Admin Control Panel', path: '/admin', icon: Shield }]
        : [];

    const displayNavItems = [...navItems, ...adminLink];

    const handleLogout = async () => {
        if (!confirmDiscardPendingWritersRoomChanges()) return;
        await logout();
        navigate('/auth/login');
    };

    // finding D-05 (extended): sidebar/mobile nav sits outside
    // WritersRoomProvider's tree, so it can't read pendingChanges directly —
    // see utils/pendingChangesGuard.js.
    const handleNavClick = (e) => {
        if (!confirmDiscardPendingWritersRoomChanges()) {
            e.preventDefault();
            return;
        }
        setIsMobileMenuOpen(false);
    };

    // ── Billing banner ────────────────────────────────────────────────────────
    const billingBanner = useMemo(() => {
        if (!user) return null;
        const status = user.stripe_status;
        const tier = user.plan_tier;
        // Only show for paid-tier users with payment problems
        if (!tier || tier === 'Wanderer' || !status) return null;

        if (status === 'past_due') {
            const graceUntil = user.grace_until ? new Date(user.grace_until) : null;
            const now = new Date();
            const inGrace = graceUntil && graceUntil > now;

            if (inGrace) {
                const dateStr = graceUntil.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                return (
                    <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start text-center sm:text-left gap-2 sm:gap-3 px-4 py-3 sm:py-2.5 bg-amber-500/10 border-b border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm">
                        <AlertTriangle className="w-5 h-5 sm:w-4 sm:h-4 shrink-0" />
                        <span>
                            <strong>Payment issue detected</strong> — you have until <strong>{dateStr}</strong> before script creation is paused.
                            {' '}<a href="/dashboard" className="underline font-semibold hover:opacity-80">Update billing →</a>
                        </span>
                    </div>
                );
            } else {
                // Grace expired
                return (
                    <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start text-center sm:text-left gap-2 sm:gap-3 px-4 py-3 sm:py-2.5 bg-red-500/10 border-b border-red-500/30 text-red-700 dark:text-red-300 text-sm">
                        <AlertCircle className="w-5 h-5 sm:w-4 sm:h-4 shrink-0" />
                        <span>
                            <strong>Billing issue</strong> — new script creation is locked until payment succeeds.
                            {' '}<a href="/dashboard" className="underline font-semibold hover:opacity-80">Update billing →</a>
                        </span>
                    </div>
                );
            }
        }

        if (status === 'unpaid' || status === 'incomplete') {
            return (
                <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start text-center sm:text-left gap-2 sm:gap-3 px-4 py-3 sm:py-2.5 bg-red-500/10 border-b border-red-500/30 text-red-700 dark:text-red-300 text-sm">
                    <AlertCircle className="w-5 h-5 sm:w-4 sm:h-4 shrink-0" />
                    <span>
                        <strong>Billing issue</strong> — new script creation is locked until payment succeeds.
                        {' '}<a href="/dashboard" className="underline font-semibold hover:opacity-80">Update billing →</a>
                    </span>
                </div>
            );
        }

        return null;
    }, [user]);
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-background flex flex-col md:flex-row">
            {user && !user.has_completed_onboarding && (
                <OnboardingTour onComplete={() => completeOnboarding()} />
            )}
            {/* Mobile Header */}
            <header className="md:hidden glass sticky top-0 z-50 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-premium flex items-center justify-center shadow-lg shadow-primary/20">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-bold text-lg tracking-tight text-premium block sm:hidden">NCN Engine</span>
                    <span className="font-bold text-lg tracking-tight text-premium hidden sm:block">Niche Content Navigator</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                    {isMobileMenuOpen ? <X /> : <Menu />}
                </Button>
            </header>

            {/* Sidebar - Desktop & Mobile Overlay */}
            <aside className={cn(
                "fixed inset-y-0 left-0 z-40 w-64 glass transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 pt-20 md:pt-0",
                isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="h-full flex flex-col p-4">
                    {/* Logo Section */}
                    <div className="hidden md:flex items-center gap-3 px-2 mb-8 animate-in fade-in slide-in-from-left duration-700">
                        <div className="w-10 h-10 rounded-xl bg-premium flex items-center justify-center shadow-lg shadow-primary/30 animate-float">
                            <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-xl tracking-tight leading-none text-premium">Niche Content Navigator</h1>
                            <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-widest font-semibold opacity-70">EndoCore Engine</p>
                        </div>
                    </div>

                    {/* Nav Items */}
                    <nav className="flex-1 space-y-1">
                        {displayNavItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={handleNavClick}
                                className={({ isActive }) => cn(
                                    "nav-item",
                                    isActive && "active"
                                )}
                            >
                                <item.icon className="w-5 h-5" />
                                <span>{item.name}</span>
                            </NavLink>
                        ))}
                    </nav>

                    {/* Bottom Actions */}
                    <div className="mt-auto space-y-1 pt-4 border-t border-border/50">
                        <button
                            onClick={() => setProfileOpen(true)}
                            className="nav-item w-full"
                        >
                            <User className="w-5 h-5" />
                            <span>Profile</span>
                        </button>
                        <button
                            onClick={() => setSettingsOpen(true)}
                            className="nav-item w-full"
                        >
                            <Settings className="w-5 h-5" />
                            <span>Settings</span>
                        </button>
                        <button
                            onClick={handleLogout}
                            className="nav-item w-full text-red-500 hover:bg-red-500/10 hover:text-red-600 transition-colors"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Logout</span>
                        </button>

                        {/* User Info Card */}
                        <div className="mt-4 p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3 glass relative overflow-hidden group hover:border-primary/30 transition-all duration-500">
                            <div className="absolute inset-0 bg-premium opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
                            <div className="w-10 h-10 rounded-full bg-premium flex items-center justify-center text-white font-black text-xs shadow-glow relative z-10">
                                {user?.name?.charAt(0) || 'U'}
                            </div>
                            <div className="flex-1 min-w-0 relative z-10">
                                <p className="text-[clamp(0.875rem,3vw,1rem)] font-bold whitespace-normal break-words leading-tight">{user?.name || 'User'}</p>
                                    <div className="flex items-center justify-between gap-1 overflow-hidden">
                                        <p className="text-[10px] text-muted-foreground whitespace-normal break-words font-semibold tracking-tighter shrink-0">{user?.plan_tier}</p>
                                        {user?.entitlements && user.plan_tier !== 'Endovelicus' && (
                                            <p className="text-[10px] text-muted-foreground font-bold shrink-0 whitespace-normal break-words ml-auto" title={`Base: ${user.entitlements.base_scripts}${user.entitlements.rollover_scripts ? ` + Rollover: ${user.entitlements.rollover_scripts}` : ''}${user.entitlements.extra_scripts ? ` + Extra: ${user.entitlements.extra_scripts}` : ''}`}>
                                            {user.entitlements.scripts_used} / {user.entitlements.base_scripts + (user.entitlements.rollover_scripts || 0) + (user.entitlements.extra_scripts || 0)}
                                            {(user.entitlements.rollover_scripts > 0 || user.entitlements.extra_scripts > 0) && <span className="text-[8px] text-primary ml-0.5">+</span>}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col w-full max-w-[100vw] overflow-x-clip bg-slate-50/50 dark:bg-background/50 pb-28 md:pb-0">
                {billingBanner}
                <div className="flex-1 w-full mx-auto p-4 md:p-6 lg:p-8">
                    {children}
                </div>
                <Footer />
            </main>

            {/* Mobile Bottom Navigation */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-6 pt-2 safe-area-bottom">
                <div className="glass rounded-2xl flex items-center justify-around p-2 shadow-2xl border-white/10 ring-1 ring-white/5 backdrop-blur-xl bg-background/80">
                    {displayNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={handleNavClick}
                            className={({ isActive }) => cn(
                                "flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-300 min-w-[64px]",
                                isActive ? "bg-premium text-white shadow-glow scale-105" : "text-muted-foreground opacity-70"
                            )}
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="text-[10px] font-black uppercase tracking-tighter whitespace-normal break-words text-center w-full">{item.name.replace('Writers Room', 'Editor').replace('Admin Control Panel', 'Admin')}</span>
                        </NavLink>
                    ))}
                    <button
                        onClick={() => setSettingsOpen(true)}
                        className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl text-muted-foreground opacity-70 min-w-[64px]"
                    >
                        <Settings className="w-5 h-5" />
                        <span className="text-[10px] font-black uppercase tracking-tighter">Settings</span>
                    </button>
                    <button
                        onClick={() => setProfileOpen(true)}
                        className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl text-muted-foreground opacity-70 min-w-[64px]"
                    >
                        <div className="w-5 h-5 rounded-full bg-premium flex items-center justify-center text-[8px] text-white font-black">
                            {user?.name?.charAt(0) || 'U'}
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-tighter">Profile</span>
                    </button>
                </div>
            </div>

            {/* Dialogs */}
            <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
            <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        </div>
    );
};

export default Shell;
